import {
  CookedEvent,
  DatalossSource,
  DatalossType,
  EVENT_COOKED_BACKGROUNDED,
  EVENT_COOKED_CHANGE,
  EVENT_COOKED_CLICK,
  EVENT_COOKED_CONSENT,
  EVENT_COOKED_CONSOLE_LOG,
  EVENT_COOKED_COPY,
  EVENT_COOKED_CRASHED,
  EVENT_COOKED_CUSTOM,
  EVENT_COOKED_CUMULATIVE_LAYOUT_SHIFT,
  EVENT_COOKED_DATALOSS,
  EVENT_COOKED_ELEM_SEEN,
  EVENT_COOKED_ERROR,
  EVENT_COOKED_EXCEPTION,
  EVENT_COOKED_FIRST_INPUT_DELAY,
  EVENT_COOKED_FOCUS,
  EVENT_COOKED_HIGHLIGHT,
  EVENT_COOKED_KEYBOARD_CLOSE,
  EVENT_COOKED_KEYBOARD_OPEN,
  EVENT_COOKED_LOAD,
  EVENT_COOKED_LOAD_MELTED,
  EVENT_COOKED_LONG_CLICK,
  EVENT_COOKED_LOW_MEMORY,
  EVENT_COOKED_MOUSE_THRASH,
  EVENT_COOKED_NAVIGATE,
  EVENT_COOKED_PAGEVAR,
  EVENT_COOKED_PAGEVIEW,
  EVENT_COOKED_PASTE,
  EVENT_COOKED_PINCH_GESTURE,
  EVENT_COOKED_REDACTED,
  EVENT_COOKED_REQUEST,
  EVENT_COOKED_TAP,
  EVENT_COOKED_UNHANDLED_CLICK,
  EVENT_COOKED_UNHANDLED_LONG_CLICK,
  EVENT_COOKED_USERVAR,
  EVENT_FORM_ABANDONED,
  EventLowMemoryDetails,
  NavigationReason,
  Note,
  PinchGestureDetails,
  PinchGestureType,
  PageTypeEnum,
  ReasonTok,
  ConsoleMessageLevelType,
  WatchedElementType,
  Flags,
  EVENT_COOKED_CONSOLE_MESSAGE,
  EVENT_COOKED_KEEP,
  EVENT_COOKED_INTERACTION_TO_NEXT_PAINT,
  EVENT_COOKED_ELEMVAR_ERROR,
  DevToolsMode,
} from '@fullstory/client-api';
import {
  DEAD_CLICK,
  ERROR_CLICK,
  RAGE_CLICK,
  getClassOrId,
  getClassOrIdStruct,
  LONG_CLICK,
  PageModel,
  SessionModel,
  UNHANDLED_CLICK,
} from '@fullstory/playback-model';
import { activityNameFromPath } from '@fullstory/util/session';
import {
  DatalossTarget,
  DatalossElementTarget,
  DatalossNetworkTarget,
  FAKE_EVENT_COOKED_REFERRED_BY,
  FAKE_EVENT_COOKED_CHANGE_TAB,
  FAKE_EVENT_COOKED_CLOSE_TAB,
  FAKE_EVENT_COOKED_NEW_TAB,
  FakeCookedEvent,
  FakeCookedEventType,
  FakeCookedEventDetails,
} from './types';
import { bestUnitFitFromMs, chopDecimalDigits } from '@fullstory/util/fmt';
import { EventListEvent, EventListNote } from './reducers';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { intl } from '@fullstory/intl';
import { DetectionsConfigurationRules } from '@fullstory/proto/fs/services/ingestion-settings';
import { cssEscapeIdent } from '@fullstory/selectors';
import { DevToolsMode as DevToolsModeProto } from '@fullstory/proto/fs/services/playback/highlight.proto';
import { assertExhaustive } from '@fullstory/typeutils';

const createFakeCookedEvent = (
  when: number,
  type: FakeCookedEventType,
  page: Readonly<PageModel> | undefined,
  details: FakeCookedEventDetails = {},
): FakeCookedEvent => ({
  PageUrl: page?.url ?? '',
  PageTitle: '',
  Type: type,
  When: when,
  ...details,
});

export const cookedEventText = (
  e: CookedEvent | FakeCookedEvent,
  params: {
    isFirstInPage: boolean;
    pageType: PageTypeEnum;
  },
): { action: string; target?: string; data?: string } => {
  const { isFirstInPage, pageType } = params ?? {};
  const targetSelector = e.TargetSelector
    ? getClassOrId(e.TargetSelector)
    : e.TargetSelectorStruct
    ? getClassOrIdStruct(e.TargetSelectorStruct)
    : '';
  const errs = formatEventVarErrs(e.EventVarErrs);
  const target = errs || targetSelector;

  switch (e.Type) {
    case EVENT_COOKED_CLICK: {
      if (!e.Modifiers) {
        const action = intl.formatMessage({ defaultMessage: 'Clicked', id: '+g7iCc' });
        return { action, target };
      }
      if (e.Modifiers[UNHANDLED_CLICK] && e.Modifiers[LONG_CLICK] && !e.Modifiers[ERROR_CLICK]) {
        const action = intl.formatMessage({ defaultMessage: 'Unhandled long click', id: 'zlUYlI' });
        return { action, target };
      }
      if (e.Modifiers[UNHANDLED_CLICK] && !e.Modifiers[LONG_CLICK] && !e.Modifiers[ERROR_CLICK]) {
        const action = intl.formatMessage({ defaultMessage: 'Unhandled click', id: 'C2yUg7' });
        return { action, target };
      }

      if (e.Modifiers[LONG_CLICK] && !e.Modifiers[UNHANDLED_CLICK] && !e.Modifiers[ERROR_CLICK]) {
        const action = intl.formatMessage({ defaultMessage: 'Long click', id: '9ex9he' });
        return { action, target };
      }

      if (e.Modifiers[ERROR_CLICK] && e.Modifiers[UNHANDLED_CLICK] && e.Modifiers[LONG_CLICK]) {
        const action =
          pageType === PageTypeEnum.PageTypeWeb
            ? intl.formatMessage({
                defaultMessage: 'Unhandled long click with JS Error',
                id: 'Q+tffR',
              })
            : intl.formatMessage({
                defaultMessage: 'Unhandled long click with console error',
                id: 'UDaax7',
              });
        return { action, target };
      }

      if (e.Modifiers[ERROR_CLICK] && e.Modifiers[UNHANDLED_CLICK] && !e.Modifiers[LONG_CLICK]) {
        const action =
          pageType === PageTypeEnum.PageTypeWeb
            ? intl.formatMessage({
                defaultMessage: 'Unhandled click with JS Error',
                id: 'yDOzbg',
              })
            : intl.formatMessage({
                defaultMessage: 'Unhandled click with console error',
                id: 'typ8SW',
              });
        return { action, target };
      }

      if (e.Modifiers[ERROR_CLICK] && e.Modifiers[LONG_CLICK] && !e.Modifiers[UNHANDLED_CLICK]) {
        const action =
          pageType === PageTypeEnum.PageTypeWeb
            ? intl.formatMessage({ defaultMessage: 'Long click with JS Error', id: 'Sg3Tw2' })
            : intl.formatMessage({
                defaultMessage: 'Long click with console error',
                id: 'wZjmJL',
              });
        return { action, target };
      }
      const action = intl.formatMessage({ defaultMessage: 'Clicked', id: '+g7iCc' });
      return { action, target };
    }
    case EVENT_COOKED_ELEM_SEEN: {
      const watchedDetails = e.WatchedElementDetails;
      return {
        action: errs
          ? intl.formatMessage({ defaultMessage: 'Watched Element', id: 'Xt+LqS' })
          : watchedDetails?.Type == WatchedElementType.WATCHED_ELEMENT_TYPE_RENDERED
          ? intl.formatMessage({ defaultMessage: 'Rendered', id: 'W9OZNv' })
          : intl.formatMessage({ defaultMessage: 'Saw', id: '0mZ0YX' }),
        target,
      };
    }
    case EVENT_COOKED_TAP:
      return {
        action: intl.formatMessage({ defaultMessage: 'Tapped', id: '1jjzF/' }),
        target,
      };
    case EVENT_COOKED_LONG_CLICK: // deprecated
      return {
        action: intl.formatMessage({ defaultMessage: 'Long clicked', id: 'QLTdLN' }),
        target,
      };
    case EVENT_COOKED_UNHANDLED_CLICK: // deprecated
      return {
        action: intl.formatMessage({ defaultMessage: 'Unhandled click', id: 'C2yUg7' }),
        target,
      };
    case EVENT_COOKED_UNHANDLED_LONG_CLICK: // deprecated
      return {
        action: intl.formatMessage({ defaultMessage: 'Unhandled long click', id: 'zlUYlI' }),
        target,
      };
    case EVENT_COOKED_BACKGROUNDED:
      return {
        action: intl.formatMessage({ defaultMessage: 'Backgrounded', id: '33Rtlq' }),
        target: pageUrlFor(e, pageType),
      };
    case EVENT_COOKED_EXCEPTION:
      return {
        action: `Uncaught Exception:`,
        target: `${
          e.ExceptionDetails && e.ExceptionDetails.SourceFile
            ? `${e.ExceptionDetails.SourceFile} `
            : ''
        }"${e.TargetText}"`,
      };
    case EVENT_COOKED_CONSOLE_MESSAGE: {
      return {
        action: intl.formatMessage({ defaultMessage: 'Console error:', id: 'HdqYhF' }),
        target: errs || `"${e.TargetText}"`,
      };
    }
    case EVENT_COOKED_HIGHLIGHT:
      return {
        action: intl.formatMessage({ defaultMessage: 'Highlighted', id: 'Qa6Aho' }),
        target,
        data: emptyIfNullishOrMasked(e.TargetText),
      };
    case EVENT_COOKED_COPY:
      return {
        action: intl.formatMessage({ defaultMessage: 'Copied', id: 'p556q3' }),
        target,
        data: emptyIfNullishOrMasked(e.TargetText),
      };
    case EVENT_COOKED_PASTE: {
      return {
        action: intl.formatMessage({ defaultMessage: 'Pasted', id: 'PJEXr1' }),
        target,
        data: emptyIfNullishOrMasked(e.TargetText),
      };
    }
    case EVENT_COOKED_CRASHED:
      return {
        action: intl.formatMessage({ defaultMessage: 'Crashed', id: '0ZzC+U' }),
        target: pageUrlFor(e, pageType),
      };
    case EVENT_COOKED_FOCUS:
      return {
        action: intl.formatMessage({ defaultMessage: 'Focused', id: 'y0aWFY' }),
        target,
      };
    case EVENT_COOKED_CHANGE:
      return {
        action: intl.formatMessage({ defaultMessage: 'Changed', id: 'PLUzwy' }),
        target,
        data: emptyIfNullishOrMasked(e.TargetText),
      };
    case EVENT_FORM_ABANDONED:
      return {
        action: intl.formatMessage({ defaultMessage: 'Abandoned form', id: 'IZo5du' }),
        target,
      };
    case EVENT_COOKED_NAVIGATE: {
      const actionText =
        e?.NavigateDetails?.Reason == NavigationReason.NAVIGATION_REASON_RELOAD
          ? `${intl.formatMessage({ defaultMessage: 'Refresh', id: 'rELDbB' })} `
          : isFirstInPage
          ? `${intl.formatMessage({ defaultMessage: 'Visit', id: 'wOG0Sd' })} `
          : `${intl.formatMessage({ defaultMessage: 'Navigate', id: '0h4MAj' })} `;
      return {
        action: actionText,
        target: errs || pageUrlFor(e, pageType),
      };
    }
    case EVENT_COOKED_MOUSE_THRASH:
      return { action: intl.formatMessage({ defaultMessage: 'Mouse Thrashed', id: 'rsjDLW' }) };
    case EVENT_COOKED_LOAD:
    case EVENT_COOKED_LOAD_MELTED:
      return {
        action: intl.formatMessage({ defaultMessage: 'Load', id: '8EGRF9' }),
        target: pageUrlFor(e, pageType),
        data: intl.formatNumber(e.LoadEventTime ?? 0, {
          unit: 'millisecond',
          unitDisplay: 'short',
          style: 'unit',
        }),
      };
    case EVENT_COOKED_REQUEST:
      return {
        action: intl.formatMessage({ defaultMessage: 'Network Error', id: 'Ddub4p' }),
        target: e.ReqUrl,
        data: intl.formatMessage(
          {
            defaultMessage:
              '{ReqMethod} {ReqUrl}{linebreak}Status: {ReqStatus, number}{linebreak}Duration: {ReqDuration, number} ms',
            id: 'i07/dU',
            description:
              'ReqMethod is an HTTP Request Method, ReqURL is a website URL, ReqStatus is an HTTP Request Status code, ReqDuration is a number, ms signifies milliseconds. Linebreak is used to format a line break',
          },
          {
            ReqMethod: e.ReqMethod,
            ReqUrl: e.ReqUrl,
            ReqStatus: e.ReqStatus ?? 0, // json serialization drops values of 0, which for status codes indicate a network error
            ReqDuration: e.ReqDuration,
            linebreak: '\n',
          },
        ),
      };
    case EVENT_COOKED_CUSTOM: {
      let eventName = e.EventCustomName;
      if (!eventName) {
        eventName = intl.formatMessage({ defaultMessage: 'Custom Event', id: 'EZee9x' });
        if (e.EventVarErrs?.length == 0) {
          console.error(
            `Expected non-error custom event to have property \`.EventCustomName\` but got ${e.EventCustomName} instead.`,
          );
        }
      }
      return {
        action: eventName,
        target: formatVars(e.EventVars) + errs,
      };
    }
    case EVENT_COOKED_USERVAR: {
      if (e.EventVars === '{}') {
        return {
          action: intl.formatMessage({
            defaultMessage: 'Set User Properties (empty)',
            id: 'Dl/rKu',
          }),
          data: intl.formatMessage({
            defaultMessage: 'No user properties were set.',
            id: '0n9CBQ',
          }),
        };
      }
      const userVars = formatUserVars(e.EventVars);
      return {
        action: userVars.isIdentify
          ? intl.formatMessage({ defaultMessage: 'Identify User', id: 'fQsGgo' })
          : intl.formatMessage({ defaultMessage: 'Set User Properties', id: 'maReYG' }),
        data: userVars.formatted + errs,
      };
    }
    case EVENT_COOKED_PAGEVAR: {
      if (e.EventVars === '{}') {
        return {
          action: intl.formatMessage({ defaultMessage: 'Set Page Properties', id: 'KMxtPX' }),
          data: intl.formatMessage({
            defaultMessage: 'No page properties were set.',
            id: 'xH3lRy',
          }),
        };
      }
      const pageVars = formatPageVars(e.EventVars);
      return {
        action: pageVars.pageName,
        data: pageVars.formatted + errs,
      };
    }
    case FAKE_EVENT_COOKED_REFERRED_BY:
      return {
        action: intl.formatMessage(
          { defaultMessage: 'Referred by: {referrer}', id: 'TmGD7N' },
          { referrer: e.TargetText },
        ),
      };
    case EVENT_COOKED_KEYBOARD_OPEN:
      return {
        action: intl.formatMessage({ defaultMessage: 'Keyboard Opened', id: 'pHcyhO' }),
      };
    case EVENT_COOKED_KEYBOARD_CLOSE:
      return {
        action: intl.formatMessage({ defaultMessage: 'Keyboard Closed', id: 'D6ofox' }),
      };
    case EVENT_COOKED_LOW_MEMORY:
      return {
        action: intl.formatMessage({
          defaultMessage: 'Low Memory (Capture Stopped)',
          id: 'Tj2qJF',
        }),
        target,
        data: formatLowMemory(e.LowMemoryDetails),
      };
    case EVENT_COOKED_PINCH_GESTURE:
      return {
        action: `${intl.formatMessage({ defaultMessage: 'Pinch-to-zoom: ', id: 'OEF2eS' })} `,
        target: formatPinchGesture(e.PinchGestureDetails),
      };
    case EVENT_COOKED_DATALOSS:
      // detection event are not target selector but an object that can contain
      // a selector if in an input or element
      return {
        action: intl.formatMessage({ defaultMessage: 'Detection event', id: 'I0Pe6t' }),
        data: datalossKind(e),
        target: datalossTarget(e, targetSelector),
      };
    case EVENT_COOKED_CUMULATIVE_LAYOUT_SHIFT:
      return {
        action: intl.formatMessage({ defaultMessage: 'Cumulative Layout Shift', id: 'h6rdDs' }),
        data: e.CumulativeLayoutShiftDetails
          ? intl.formatMessage(
              { defaultMessage: 'Score: {score}', id: 'j8d+YP' },
              { score: truncAsDecimalNotation(e.CumulativeLayoutShiftDetails.Score) },
            )
          : '',
      };
    case EVENT_COOKED_FIRST_INPUT_DELAY:
      return {
        action: intl.formatMessage({ defaultMessage: 'First Input Delay', id: 'R6PebY' }),
        data: e.FirstInputDelayDetails ? bestUnitFitFromMs(e.FirstInputDelayDetails.Delay) : '',
      };
    case EVENT_COOKED_INTERACTION_TO_NEXT_PAINT:
      return {
        action: intl.formatMessage({ defaultMessage: 'Interaction to Next Paint', id: '28roqn' }),
        data: e.InteractionToNextPaintDetails
          ? bestUnitFitFromMs(e.InteractionToNextPaintDetails.Timing)
          : '',
      };
    case FAKE_EVENT_COOKED_CHANGE_TAB:
      return {
        action: `${intl.formatMessage({ defaultMessage: 'Changed Tab', id: 'lNZt/Z' })} `,
        data: pageUrlFor(e, pageType),
      };
    case FAKE_EVENT_COOKED_CLOSE_TAB:
      return {
        action: `${intl.formatMessage({ defaultMessage: 'Closed Tab', id: 'rCKPTr' })} `,
        target: pageUrlFor(e, pageType),
      };
    case FAKE_EVENT_COOKED_NEW_TAB: {
      return {
        action: `${intl.formatMessage({ defaultMessage: 'New Tab', id: 'btOI+Z' })} `,
        target: pageUrlFor(e, pageType),
      };
    }
    case EVENT_COOKED_ELEMVAR_ERROR: {
      const action = intl.formatMessage({ defaultMessage: 'Element Var Error', id: 'KKgn2Z' });
      return { action, target };
    }
    default:
      return { action: e.Type };
  }
};

function datalossTargetObjectToString(datalossTargetObject: DatalossTarget) {
  return JSON.stringify(datalossTargetObject);
}
/**
 * This is kind of a hack to get around an architecture decision to have one Event list type
 * for all event type (besides notes). This doesn't make sense since every event type contains
 * drastically different attributes and information.
 *
 * TODO: rewrite the EventList type as a discriminate union where based on the type of event
 * you know exactly what type are available.
 */
function datalossTarget(e: CookedEvent, target: string): string {
  switch (e.DatalossDetails?.Source) {
    case DatalossSource.DATALOSS_SOURCE_ELEMENT:
      return datalossTargetObjectToString({
        source: 'element',
        origin: e.DatalossDetails.Origin,
        selector: target,
        fId: e.FId,
        pIds: e.PIds,
        fsId: e.FsId,
      });
    case DatalossSource.DATALOSS_SOURCE_INPUT_ELEMENT:
      return datalossTargetObjectToString({
        source: 'input',
        origin: e.DatalossDetails.Origin,
        selector: target,
        fId: e.FId,
        pIds: e.PIds,
        fsId: e.FsId,
      });
    case DatalossSource.DATALOSS_SOURCE_NAVIGATE_URL:
      return datalossTargetObjectToString({
        source: 'page url',
        origin: e.DatalossDetails.Origin,
        url: e.PageUrl,
      });
    case DatalossSource.DATALOSS_SOURCE_NETWORK_URL:
      return datalossTargetObjectToString({
        source: 'request url',
        origin: e.DatalossDetails.Origin,
        url: e.DatalossDetails.RequestUrl,
      });
    case DatalossSource.DATALOSS_SOURCE_NETWORK_REQUEST_HEADER:
      return datalossTargetObjectToString({
        source: 'request header',
        origin: e.DatalossDetails.Origin,
        url: e.DatalossDetails.RequestUrl,
      });
    case DatalossSource.DATALOSS_SOURCE_NETWORK_RESPONSE_HEADER:
      return datalossTargetObjectToString({
        source: 'response header',
        origin: e.DatalossDetails.Origin,
        url: e.DatalossDetails.RequestUrl,
      });
    case DatalossSource.DATALOSS_SOURCE_NETWORK_REQUEST_BODY:
      return datalossTargetObjectToString({
        source: 'request body',
        origin: e.DatalossDetails.Origin,
        url: e.DatalossDetails.RequestUrl,
      });
    case DatalossSource.DATALOSS_SOURCE_NETWORK_RESPONSE_BODY:
      return datalossTargetObjectToString({
        source: 'response body',
        origin: e.DatalossDetails.Origin,
        url: e.DatalossDetails.RequestUrl,
      });
    case DatalossSource.DATALOSS_SOURCE_LOG:
      return datalossTargetObjectToString({
        source: 'console log',
      });
    case DatalossSource.DATALOSS_SOURCE_EXCEPTION:
      return datalossTargetObjectToString({
        source: 'exception',
        origin: e.DatalossDetails.Origin,
      });
  }
  return intl.formatMessage({ defaultMessage: 'unknown source', id: '+TVtYM' });
}

function datalossKind(e: CookedEvent): string {
  switch (e.DatalossDetails?.Type) {
    case DatalossType.DATALOSS_TYPE_PAYMENT_CARD:
      return intl.formatMessage({ defaultMessage: 'payment card', id: 'rXoaah' });
    case DatalossType.DATALOSS_TYPE_EMAIL:
      return intl.formatMessage({ defaultMessage: 'email', id: 'yUVl6+' });
    case DatalossType.DATALOSS_TYPE_SSN:
      return intl.formatMessage({ defaultMessage: 'social security', id: 'I3czuV' });
    case DatalossType.DATALOSS_TYPE_PASSWORD:
      return intl.formatMessage({ defaultMessage: 'password', id: 'sQnzu7' });
  }

  return intl.formatMessage({ defaultMessage: 'unknown type', id: 'PbQBla' });
}

function pageUrlFor(evt: CookedEvent, pageType: PageTypeEnum): string {
  let url: string;
  try {
    url = decodeURI(evt.PageUrl);
  } catch (e) {
    url = evt.PageUrl;
  }

  switch (pageType) {
    case PageTypeEnum.PageTypeAndroid:
      return activityNameFromPath(url);
    default:
      return url;
  }
}

function emptyIfNullishOrMasked(text: string | undefined): string {
  // Convert null-ish to empty string.
  if (!text) {
    return '';
  }

  // Show masked text as such.
  if (cookedTextIsRenderedMasked(text)) {
    return intl.formatMessage({ defaultMessage: 'masked', id: '/h6KDk' });
  }

  return `"${text}"`;
}

function formatLowMemory(lowMemoryDetails?: EventLowMemoryDetails): string {
  let formatted = '';

  let availableMemory = intl.formatMessage({ defaultMessage: 'unknown', id: 'uo8NOT' });
  let maxMemory = intl.formatMessage({ defaultMessage: 'unknown', id: 'uo8NOT' });

  if (lowMemoryDetails != null) {
    // convert bytes to MB for both AvailableMemory and MaxMemory
    const availMB = chopDecimalDigits(lowMemoryDetails.AvailableMemory / (1024 * 1024), 2);
    const maxMB = chopDecimalDigits(lowMemoryDetails.MaxMemory / (1024 * 1024), 2);
    availableMemory = `${availMB}`;
    maxMemory = `${maxMB}`;
  }

  formatted += `${intl.formatMessage(
    {
      defaultMessage: 'Available: {availableMemory} MB',
      id: 'eBEPbr',
      description: 'MB = Megabytes',
    },
    { availableMemory },
  )}\n`;
  formatted += `${intl.formatMessage(
    { defaultMessage: 'Max: {maxMemory} MB', id: 'lzfdao', description: 'MB = Megabytes' },
    { maxMemory },
  )}\n`;
  return formatted;
}

function formatVars(varsJson?: string): string {
  if (!varsJson) {
    return '';
  }
  const asObj = JSON.parse(varsJson);
  let formatted = '';
  for (const key in asObj) {
    formatted += `${key}: ${asObj[key]}\n`;
  }
  return formatted;
}

function formatPageVars(varsJson?: string): { formatted: string; pageName: string } {
  if (!varsJson) {
    return { formatted: '', pageName: '' };
  }
  const asObj = JSON.parse(varsJson);
  let formatted = '';
  let pageName = `Set Page Properties`;
  for (const key in asObj) {
    if (key == 'pageName') {
      pageName += `: ${asObj[key]}`;
    }
    formatted += `${key}: ${asObj[key]}\n`;
  }
  return { formatted, pageName };
}

function formatUserVars(varsJson?: string): { formatted: string; isIdentify: boolean } {
  if (!varsJson) {
    // A client error (e.g. rate limiting) prevented us from recording the payload.
    return { formatted: '', isIdentify: false };
  }

  const asObj = JSON.parse(varsJson);
  let formatted = '';
  let isIdentify = false;
  for (const key in asObj) {
    if (key === 'uid') {
      isIdentify = true;
    }
    formatted += `${key}: ${asObj[key]}\n`;
  }
  return { formatted, isIdentify };
}

function formatEventVarErrs(errs: CookedEvent['EventVarErrs']): string {
  if (errs && errs.length > 0) {
    const formatted = errs.map(err => ({
      Field: err.Field,
      Value: err.Value,
      ErrMsg: ReasonTok[err.ErrCode],
    }));
    return intl.formatMessage(
      { defaultMessage: 'Errors: {json}', id: 'Ixn7wb' },
      { json: JSON.stringify(formatted, null, 2) },
    );
  }

  return '';
}

function formatPinchGesture(pinchGestureDetails: PinchGestureDetails | undefined): string {
  if (pinchGestureDetails === undefined) {
    return '';
  }
  switch (pinchGestureDetails.PinchGestureType) {
    case PinchGestureType.PINCH_GESTURE_SCALE_IN:
      return intl.formatMessage({ defaultMessage: 'Zoomed In', id: 'rzE3qA' });
    case PinchGestureType.PINCH_GESTURE_SCALE_OUT:
      return intl.formatMessage({ defaultMessage: 'Zoomed Out', id: 'IswBht' });
    default:
      return '';
  }
}

function cookedTextIsRenderedMasked(text: string): boolean {
  return /[^\s*]/.exec(text) == null;
}

export function isNote(x: CookedEvent | Note): x is Note {
  return (x as Note).Id !== undefined;
}

function isPageVarPageEndEvent(
  e: CookedEvent,
): e is CookedEvent & { IsUnset: true; Type: typeof EVENT_COOKED_PAGEVAR } {
  const eventVars = e.EventVars ? JSON.parse(e.EventVars) : null;
  // if the event is a page var event and the page name is empty and the event occurred after the page end
  return e.Type === EVENT_COOKED_PAGEVAR && eventVars && eventVars['pageName'] === '' && e.IsUnset;
}

export const isLoadEvent = (e: CookedEvent) =>
  [EVENT_COOKED_LOAD, EVENT_COOKED_LOAD_MELTED].includes(e.Type);

/**
 * This is kind of a sudo assertion as detection event isn't actually is own event type.
 * However if this return true then it is a detection event.
 */
export const isDetectionEvent = (
  event: EventListEvent | EventListNote,
): event is EventListEvent => {
  // sanity check
  if (!event) {
    return false;
  }

  const assumeEventListEvent = event as EventListEvent;
  return !!assumeEventListEvent.Type && assumeEventListEvent.Type === EVENT_COOKED_DATALOSS;
};

/**
 * take a Event list string and check if it is parse-able and if it contains a dataloss target object
 */
export const isDetectionTargetString = (target?: string): target is string => {
  if (!target) {
    return false;
  }
  try {
    const assumeDatalossTarget = JSON.parse(target) as DatalossTarget;
    return !!assumeDatalossTarget.source;
  } catch {
    return false;
  }
};

/**
 * returns the a target object if the event is a detection event
 */
export const getDetectionTargetObject = (
  event: EventListEvent | EventListNote,
): DatalossTarget | null => {
  if (!event || !isDetectionEvent(event) || !isDetectionTargetString(event.target)) {
    return null;
  }

  return JSON.parse(event.target) as DatalossTarget;
};

export const isErrorClick = (event: EventListEvent) => {
  if (event.Type == EVENT_COOKED_CLICK && event.Modifiers) {
    for (const key in event.Modifiers) {
      if (key == ERROR_CLICK) {
        return true;
      }
    }
  }
  return false;
};

const getCookedEvents = (page: Readonly<PageModel>, session: SessionModel): CookedEvent[] => {
  const cookedEvents = [...page.cookedEvents];
  const tabEvents = (session.transitions ?? []).reduce<CookedEvent[]>((result, transition) => {
    const { absTime, type } = transition;
    const startOffset = Math.max(absTime - (page.localStartTime ?? 0), 0);

    switch (transition.type) {
      case 'close_tab': {
        const { pageId, tabId } = transition;

        if (pageId === page.pageId) {
          const tabIndex = session.tabs.findIndex(t => t.tabId === tabId);
          const fakeEvent = createFakeCookedEvent(startOffset, type, page, {
            CloseTabDetails: {
              TabIndex: tabIndex,
              TabId: tabId,
            },
          });

          return [...result, fakeEvent];
        }

        return result;
      }
      case 'change_tab': {
        const { fromTabId, toPageId, toTabId } = transition;

        if (toPageId === page.pageId) {
          const fromTabIndex = session.tabs.findIndex(t => t.tabId === fromTabId);
          const toTabIndex = session.tabs.findIndex(t => t.tabId === toTabId);
          const fakeEvent = createFakeCookedEvent(startOffset, type, page, {
            ChangeTabDetails: {
              FromTabIndex: fromTabIndex,
              FromTabId: fromTabId,
              ToTabIndex: toTabIndex,
              ToTabId: toTabId,
            },
          });

          return [...result, fakeEvent];
        }

        return result;
      }
      case 'new_tab': {
        const { pageId, tabId } = transition;

        if (pageId === page.pageId) {
          const tabIndex = session.tabs.findIndex(t => t.tabId === tabId);
          const fakeEvent = createFakeCookedEvent(startOffset, type, page, {
            NewTabDetails: { TabIndex: tabIndex, TabId: tabId },
          });

          return [...result, fakeEvent];
        }

        return result;
      }
      default:
        return result;
    }
  }, []);

  const firstCookedEvent = cookedEvents[0];
  return firstCookedEvent && page.referrer?.length
    ? [
        {
          ...firstCookedEvent,
          Type: FAKE_EVENT_COOKED_REFERRED_BY,
          TargetText: page.referrer,
        },
        ...page.cookedEvents,
        ...tabEvents,
      ]
    : [...cookedEvents, ...tabEvents];
};

export const flattenEvents = (session: SessionModel, flags: Flags | null): EventListEvent[] =>
  [...session.pages].flatMap(p => {
    const cookedEvents = getCookedEvents(p, session);
    return cookedEvents.map((e, idx) => ({
      ...e,
      ...cookedEventText(e, {
        isFirstInPage: idx === 0,
        pageType: p.type as PageTypeEnum,
      }),
      // Compute the event time relative to the page's real start time
      // ('localStartTime'), rather than the linearized start time
      // ('startTime'). The latter is shifted so that pages from tabs that were
      // open at the same time don't overlap. It's useful for the timeline, but
      // for the event list we want to use the real wall clock time.
      AbsTime: p.localStartTime + e.When,
      // LinearizedTime, not AbsTime, needs to be used in getCurrentIndex
      // NOTE: for server events, linearized time is set to the absolute time to avoid displaying
      // the event at the end of the page list. See SC-192196 for more detail.
      LinearizedTime: p.type === 'server' ? p.localStartTime + e.When : p.startTime + e.When,
      UserId: p.userId,
      SessionId: p.sessionId,
      PageId: p.pageId,
      NativePlatform: p.nativePlatform,
      Key: keyForCookedEvent(p, e, idx),
    }));
  });

/** Returns whether the event should be styled as an informational event */
export const isInfoEvent = (event: CookedEvent | Note) =>
  (event as CookedEvent).Type === FAKE_EVENT_COOKED_REFERRED_BY;

export const keyForPage = (page: Readonly<PageModel>) => page.pageId;

export const keyForCookedEvent = (
  page: Readonly<PageModel>,
  event: CookedEvent,
  index: number,
): string => `${keyForPage(page)}-${event.When}-${event.Type}-${index}`;

const eventActionText = (event: EventListEvent) => {
  if (event.action === 'Clicked') {
    // Click types that prepend modifier text
    if (event.Modifiers) {
      if (event.Modifiers[RAGE_CLICK]) {
        return intl.formatMessage({ defaultMessage: 'Rage Click', id: 'PSUo53' });
      }
      if (event.Modifiers[ERROR_CLICK]) {
        return intl.formatMessage({ defaultMessage: 'Error Click', id: '0USC1d' });
      }
      if (event.Modifiers[DEAD_CLICK]) {
        return intl.formatMessage({ defaultMessage: 'Dead Click', id: 'hsHG36' });
      }
    }
  }

  if (event.action === 'app_forced_restart') {
    return intl.formatMessage({ defaultMessage: 'Force Restart', id: 'bU5rfa' });
  }

  return event.action;
};

export const playbackEventText = (event: EventListEvent | EventListNote, actionOnly: boolean) => {
  if (isNote(event)) {
    return event.Text;
  }

  if (isPageVarPageEndEvent(event)) {
    return intl.formatMessage(
      { defaultMessage: 'Page End: {pageName}', id: 'HCZMnF' },
      {
        pageName: event.PageVars
          ? (JSON.parse(event.PageVars)['pageName'] as string) || 'Unknown'
          : 'Unknown',
      },
    );
  }

  if (actionOnly || !event.target) {
    return eventActionText(event);
  }
  // get event text for detection events
  const detectTargetInfo = getDetectionTargetObject(event);
  if (detectTargetInfo && event.data) {
    return `${event.action} (${event.data}): [${detectTargetInfo.source}]`;
  }

  return event.data
    ? `${eventActionText(event)} (${event.data}): ${event.target}`
    : `${eventActionText(event)} ${event.target}`;
};

// Formats numbers in decimal notation. Values greater than 0.1 always have 2 decimal places, and values smaller
// than 0.1 always display the first 2 non-zero digits. For example, the value 1.1 will return "1.10", and the value
// 0.000123456 will return "0.00012". The value 0 is a special case, which will always return "0".
export const truncAsDecimalNotation = (givenNum: number) => {
  if (givenNum == 0) {
    return '0';
  }
  if (givenNum > 0.1) {
    return givenNum.toFixed(2);
  }

  // numFracDigits is the number of digits required to display to the first 2 non-zero digits for clsScore.
  // For example:
  // 1 - Math.floor(Math.log(0.000012345) / Math.log(10)) == 6
  // And truncating 0.000012345.toFixed(6) == 0.000012
  const numFracDigits = 1 - Math.floor(Math.log(givenNum) / Math.log(10));
  return givenNum.toFixed(numFracDigits);
};

// Formats numbers so that values larger than 0.001 use decimal notation, and values smaller than 0.001 use
// scientific notation. Values are also truncated to 2 decimal places. For example, the value 0.01234 will return 0.012,
// while the value 0.000123456 will return 1.2E-4.
export const truncAsScientificNotation = (givenNum: number) => {
  const truncClsScore = truncAsDecimalNotation(givenNum);
  if (givenNum == 0 || givenNum > 0.001) {
    return truncClsScore;
  }

  // NB: use toUpperCase in order to display as "8.3E-6" instead of "8.3e-6"
  return parseFloat(truncClsScore).toExponential().toUpperCase();
};

export const getExpandedEventText = (event: EventListEvent, flags: Flags | null) => {
  if (isPageVarPageEndEvent(event)) {
    return formatPageVars(event.PageVars).formatted;
  }

  const detectTargetInfo = getDetectionTargetObject(event);

  if (detectTargetInfo && event.data) {
    if (detectTargetInfo.source === 'console log') {
      return `${event.action} (${event.data}): [${detectTargetInfo.source}]`;
    }
    return `${event.action} (${event.data}): [${detectTargetInfo.source} : ${
      detectTargetInfo.origin
    } - ${
      // at this point the dataloss target object is either an element or a network
      // but we do not know which.
      (detectTargetInfo as DatalossElementTarget)?.selector ||
      (detectTargetInfo as DatalossNetworkTarget)?.url
    }]`;
  }

  const mainTarget = event.TargetSelector
    ? cssEscapeIdent(event.TargetSelector)
    : event.TargetSelectorStruct
    ? getClassOrIdStruct(event.TargetSelectorStruct, true)
    : event?.target ?? '';

  const elementVars = formatVars(event.ElementVars);
  const elementVarErrors = formatEventVarErrs(event.ElementVarErrs);
  const hasElementVars = elementVars || elementVarErrors;
  const targetSep = mainTarget && hasElementVars ? '\n\n' : '';
  const target = `${mainTarget}${targetSep}${elementVars}${elementVarErrors}`;

  // if data and target show both, if just data or target show that, else show action
  return `${event?.data ?? ''} ${target}`.trim() || event.action;
};

// Only show cooked console messages that have ERROR level OR have EventVarErrs
const shouldShowConsoleEvent = (event: EventListEvent): boolean =>
  event.ConsoleMessageDetails?.Type == ConsoleMessageLevelType.CONSOLE_MESSAGE_LEVEL_TYPE_ERROR ||
  !!event.EventVarErrs?.length;

// Filter certain event types from being shown.
export const filterEvents = (
  event: EventListEvent,
  flags: Flags | null,
  detectionsConfigRules: DetectionsConfigurationRules,
) => {
  switch (event.Type) {
    case EVENT_COOKED_CUMULATIVE_LAYOUT_SHIFT:
    case EVENT_COOKED_FIRST_INPUT_DELAY:
      return flags?.hasBit(Flag.FeatDevtools) || false;
    case EVENT_COOKED_LOAD:
      return flags?.hasBit(Flag.FeatDevtools) || false;
    case EVENT_COOKED_REQUEST:
      return flags?.hasBit(Flag.FeatDevtools) || false;
    case EVENT_COOKED_CONSOLE_MESSAGE:
      // Only show cooked console messages with ERROR level
      return flags?.hasBit(Flag.FeatDevtools) && shouldShowConsoleEvent(event);
    case EVENT_COOKED_ERROR:
    case EVENT_COOKED_CONSOLE_LOG:
      return false; // Not currently used.
    case EVENT_COOKED_EXCEPTION:
      return flags?.hasBit(Flag.FeatDevtools) || false;
    case EVENT_COOKED_DATALOSS:
      return detectionsConfigRules?.detectionsEnabled || false;
    case EVENT_COOKED_ELEM_SEEN:
      // The only event we want to hide is a watched element of type end. We want to keep all others
      // even type unknown since that represents events cooked before the watched element revamp.
      return (
        !event.WatchedElementDetails ||
        event.WatchedElementDetails.Type == WatchedElementType.WATCHED_ELEMENT_TYPE_UNKNOWN ||
        event.WatchedElementDetails.Type == WatchedElementType.WATCHED_ELEMENT_TYPE_RENDERED ||
        event.WatchedElementDetails.Type == WatchedElementType.WATCHED_ELEMENT_TYPE_VISIBLE
      );
    case EVENT_COOKED_REDACTED:
      // Don't render redacted events at all, they're only there to purge the index.
      return false;
    case EVENT_COOKED_FOCUS:
      return false;
    case EVENT_COOKED_PAGEVIEW:
      return false;
    case EVENT_COOKED_CONSENT:
      // Don't render consent event, they are used for LTA redaction only.
      return false;
    case EVENT_COOKED_KEEP:
      // Don't render keep event, they are only used to help with initial configuration and debugging of keep
      // rules.
      return false;
    default:
      return true;
  }
};

export const toDevToolsModeProto = (mode: DevToolsMode): DevToolsModeProto => {
  switch (mode) {
    case DevToolsMode.None:
      return DevToolsModeProto.DEV_TOOLS_MODE_NONE;
    case DevToolsMode.Console:
      return DevToolsModeProto.DEV_TOOLS_MODE_CONSOLE;
    case DevToolsMode.Network:
      return DevToolsModeProto.DEV_TOOLS_MODE_NETWORK;
    default:
      assertExhaustive(mode);
  }
};
